import { computed, signal, type Signal } from '@angular/core';
import { meetupTitles } from 'src/lib/constants/meetup-data';
import type {
	ClassroomActivityModel,
	ClassroomPeriodModel,
} from 'src/lib/services/api/classroom-manager/models/classroom.model';
import type { MeetupManagementListItemModel } from 'src/lib/services/api/organizations/meetups/meetup-management.model';
import type { StudentCurrentCourseModel } from 'src/lib/services/api/students/classes/current-courses/student-current-course.model';
import {
	StudentCourseActivitiesModel,
	StudentCourseActivityFolderModel,
} from 'src/lib/services/api/students/classes/student-course.model';
import {
	getWonderCharacterForSubjectKey,
	type WonderCharacterKey,
	type WonderCharacterSubjectKey,
} from 'src/lib/utilities/wonder/wonder-character.utilities';
import 'temporal-polyfill/global';

export type WonderActivityScheduleItemStatus = 'current' | 'past' | 'upcoming';

export type WonderActivityScheduleItemType =
	| 'courseHub'
	| 'courseLesson'
	| 'meetupPhysical'
	| 'meetupVirtual'
	| 'noSchool'
	| 'noSchool'
	| 'pod'
	| 'recess'
	| 'skillBuilders'
	| 'study'
	| 'unstructured';

export type WonderActivityScheduleItemResourceLink =
	| {
			href: string;
			target: '_blank';
			type: 'href';
	  }
	| {
			route: string | unknown[];
			type: 'route';
	  }
	| {
			meetup: MeetupManagementListItemModel;
			modalType: 'meetupAttendance';
			type: 'modal';
	  };

export interface WonderActivityActionModal {
	meetup: MeetupManagementListItemModel;
	type: 'meetupAttendance';
}

export type WonderActivityScheduleItemSource =
	| 'classroom'
	| 'meetup'
	| 'system';

export function buildWonderActivityTimeSignals(
	item: WonderActivityScheduleItem,
	currentTime: Signal<Temporal.ZonedDateTime>,
): WonderActivityTimeSignals {
	function status(
		now: Temporal.ZonedDateTime,
	): WonderActivityScheduleItemStatus {
		if (Temporal.ZonedDateTime.compare(now, item.startDate) < 0) {
			return 'upcoming';
		}

		if (Temporal.ZonedDateTime.compare(now, item.endDate) >= 0) {
			return 'past';
		}

		return 'current';
	}

	function getMillisecondsBetween(
		left: Temporal.ZonedDateTime,
		right: Temporal.ZonedDateTime,
	): number {
		return Number(
			(left.toInstant().epochNanoseconds - right.toInstant().epochNanoseconds) /
				BigInt(1000000),
		);
	}

	const startsInMs = computed(() =>
		getMillisecondsBetween(item.startDate, currentTime()),
	);
	const endedMsAgo = computed(() =>
		getMillisecondsBetween(currentTime(), item.endDate),
	);

	return {
		endedMsAgo,
		isCurrent: computed(() => status(currentTime()) === 'current'),
		isPast: computed(() => status(currentTime()) === 'past'),
		startedMsAgo: computed(() => -startsInMs()),
		startsInMs,
		status: computed(() => status(currentTime())),
	};
}

export function getWonderActivityCharacter(
	subject: string | null | undefined,
): WonderCharacterKey {
	if (subject == null) {
		return 'parrot';
	}

	return getWonderCharacterForSubjectKey(getWonderActivitySubjectKey(subject));
}

export function getWonderActivitySubjectKey(
	subject: string,
): WonderCharacterSubjectKey {
	subject = subject.toLowerCase().trim();

	if (
		subject.includes('english') ||
		subject.includes('language arts') ||
		subject.includes('reading')
	) {
		return 'english';
	} else if (
		subject.includes('health') ||
		subject.includes('wellness') ||
		subject.includes('fitness')
	) {
		return 'health';
	} else if (subject.includes('math')) {
		return 'math';
	} else if (subject.includes('science')) {
		return 'science';
	} else if (subject.includes('social')) {
		return 'social';
	} else if (subject.includes('art')) {
		return 'art';
	}

	return 'english';
}

export type WonderActivityScheduleWeek = Record<
	number,
	WonderActivityScheduleItem[]
>;

export interface WonderActivityScheduleCourseActivities {
	activities: StudentCourseActivitiesModel | null;
	course: StudentCurrentCourseModel;
}

export interface WonderActivityScheduleMonth {
	month: number;
	year: number;
}

export interface WonderWeeklyProgressItem {
	activity: StudentCourseActivityFolderModel;
	completed: boolean;
	course: StudentCurrentCourseModel;
	dayIndex: number;
	gradeLabel: string;
	latestFeedback: { message?: string } | null;
	locked: boolean;
	registrationId: number;
	route: unknown[];
	scheduleOrder: number;
	scheduleTrackId: string;
	statusClass: string;
	statusIcon: string;
	statusLabel: string;
	subjectKey: WonderCharacterSubjectKey;
	subjectLabel: string;
	trackId: string;
}

export interface WonderWeeklyProgressDay {
	date: Temporal.ZonedDateTime;
	index: number;
	isInSession: boolean;
	isSchoolClosed: boolean;
	label: string;
	items: WonderWeeklyProgressItem[];
}

export interface WonderActivityTimeSignals {
	endedMsAgo: Signal<number>;
	isCurrent: Signal<boolean>;
	isPast: Signal<boolean>;
	startedMsAgo: Signal<number>;
	startsInMs: Signal<number>;
	status: Signal<WonderActivityScheduleItemStatus>;
}

export abstract class WonderActivityScheduleItem {
	public abstract readonly type: WonderActivityScheduleItemType;
	public abstract get label(): string;
	public abstract get educator(): string | null;
	public abstract get resourceLink(): WonderActivityScheduleItemResourceLink | null;
	public abstract get subject(): string | null;
	public readonly classroomActivity: ClassroomActivityModel | null;
	public readonly moment: WonderActivityTimeSignals;
	public readonly period: ClassroomPeriodModel | null;
	public readonly source: WonderActivityScheduleItemSource;

	protected constructor(
		public readonly startDate: Temporal.ZonedDateTime,
		public readonly endDate: Temporal.ZonedDateTime,
		public readonly id: string | number,
		options: {
			classroomActivity?: ClassroomActivityModel | null;
			currentTime: Signal<Temporal.ZonedDateTime>;
			period?: ClassroomPeriodModel | null;
			source: WonderActivityScheduleItemSource;
		},
	) {
		this.classroomActivity = options.classroomActivity ?? null;
		this.period = options.period ?? null;
		this.source = options.source;
		this.moment = buildWonderActivityTimeSignals(this, options.currentTime);
	}

	public get activityId(): number | null {
		return this.classroomActivity?.activity_id ?? null;
	}

	public get day(): number {
		return this.startDate.dayOfWeek;
	}

	public get description(): string | null {
		return this.classroomActivity?.description?.trim() || null;
	}

	// eslint-disable-next-line @typescript-eslint/class-literal-property-style
	public get registrationId(): number | null {
		return null;
	}

	public get trackId(): string {
		return `${this.type}:${this.id}`;
	}

	public getCharacter(): WonderCharacterKey {
		return getWonderActivityCharacter(this.subject);
	}

	public getSubjectKey(): WonderCharacterSubjectKey | null {
		return this.subject == null
			? null
			: getWonderActivitySubjectKey(this.subject);
	}
}

/**
 * Scheduled independent course work, such as a student's Your Turn block.
 */
export class WonderCourseLessonScheduleItem extends WonderActivityScheduleItem {
	public readonly type = 'courseLesson';

	constructor(
		startDate: Temporal.ZonedDateTime,
		endDate: Temporal.ZonedDateTime,
		id: string | number,
		public readonly course: StudentCurrentCourseModel,
		public readonly targetActivity: StudentCourseActivityFolderModel | null,
		public readonly activities: readonly StudentCourseActivityFolderModel[],
		classroomActivity: ClassroomActivityModel,
		period: ClassroomPeriodModel,
		currentTime: Signal<Temporal.ZonedDateTime>,
	) {
		super(startDate, endDate, id, {
			classroomActivity,
			currentTime,
			period,
			source: 'classroom',
		});
	}

	public get educator(): string | null {
		return this.course.teacher?.name?.trim() || null;
	}

	public get label(): string {
		return this.subject;
	}

	public get resourceLink(): WonderActivityScheduleItemResourceLink | null {
		return this.course.registration_id == null
			? null
			: {
					route:
						this.targetActivity == null
							? '/weekly-progress'
							: [
									'/courses',
									this.course.registration_id,
									'activity-player',
									this.targetActivity.id,
								],
					type: 'route',
				};
	}

	public get subject(): string {
		return this.course.subject?.trim() || this.course.title?.trim() || 'Course';
	}

	public override get registrationId(): number | null {
		return this.course.registration_id ?? null;
	}
}

/**
 * Scheduled live course support, such as a Learning Hub block.
 */
export class WonderCourseHubScheduleItem extends WonderActivityScheduleItem {
	public readonly type = 'courseHub';

	constructor(
		startDate: Temporal.ZonedDateTime,
		endDate: Temporal.ZonedDateTime,
		id: string | number,
		public readonly course: StudentCurrentCourseModel,
		public readonly meetup: MeetupManagementListItemModel | null,
		classroomActivity: ClassroomActivityModel,
		period: ClassroomPeriodModel,
		currentTime: Signal<Temporal.ZonedDateTime>,
	) {
		super(startDate, endDate, id, {
			classroomActivity,
			currentTime,
			period,
			source: 'classroom',
		});
	}

	public get educator(): string | null {
		return (
			this.meetup?.host?.name?.trim() ||
			this.classroomActivity?.associated_meetup_host_name?.trim() ||
			null
		);
	}

	public get label(): string {
		return this.subject;
	}

	public get resourceLink(): WonderActivityScheduleItemResourceLink | null {
		if (this.meetup?.type === 'virtual') {
			return {
				href: this.meetup.link,
				target: '_blank',
				type: 'href',
			};
		} else if (this.meetup?.type === 'physical') {
			return {
				meetup: this.meetup,
				modalType: 'meetupAttendance',
				type: 'modal',
			};
		}

		const href =
			this.classroomActivity?.meetup_link?.trim() ||
			this.classroomActivity?.associated_meetup_link?.trim();

		return href == null || href === ''
			? null
			: {
					href,
					target: '_blank',
					type: 'href',
				};
	}

	public get subject(): string {
		return this.course.subject?.trim() || this.course.title?.trim() || 'Course';
	}

	public override get registrationId(): number | null {
		return this.course.registration_id ?? null;
	}
}

/**
 * Fallback independent work when the student has no active scheduled item.
 */
export class WonderSkillBuildersScheduleItem extends WonderActivityScheduleItem {
	public readonly type = 'skillBuilders';
	public readonly educator = null;
	public override readonly moment: WonderActivityTimeSignals;
	public readonly resourceLink: WonderActivityScheduleItemResourceLink = {
		route: '/weekly-progress',
		type: 'route',
	};
	public readonly subject = null;

	constructor(
		private daysActivities: WonderActivityScheduleItem[],
		currentTime: Signal<Temporal.ZonedDateTime>,
	) {
		const lastItem = daysActivities[daysActivities.length - 1];
		const id = `skill-builders-${lastItem.endDate.dayOfYear}`;

		super(
			lastItem.endDate,
			lastItem.endDate.with({
				hour: 20,
				minute: 0,
				second: 0,
				millisecond: 0,
			}),
			id,
			{ currentTime, source: 'system' },
		);

		this.moment = this.skillBuildersMoment(currentTime);
	}

	public readonly label = 'Skill Builders';

	private skillBuildersMoment(
		currentTime: Signal<Temporal.ZonedDateTime>,
	): WonderActivityTimeSignals {
		function getMillisecondsBetween(
			left: Temporal.ZonedDateTime,
			right: Temporal.ZonedDateTime,
		): number {
			return Number(
				(left.toInstant().epochNanoseconds -
					right.toInstant().epochNanoseconds) /
					BigInt(1000000),
			);
		}

		const currentItem = computed(() => {
			return this.daysActivities
				.filter((activity) => activity.type !== 'skillBuilders') // we have to prefilter out skill builders or we infinite loop
				.find((activity) => {
					return activity.moment.isCurrent();
				});
		});

		const startsInMs = computed(() => {
			const ci = currentItem();
			if (!ci) return 0;
			else return getMillisecondsBetween(ci.endDate, currentTime());
		});
		const endedMsAgo = signal(-Infinity); // Skill Builders never end, so this is always negative.

		return {
			endedMsAgo: endedMsAgo,
			isCurrent: computed(() => currentItem() == null),
			isPast: computed(() => false), // Skill Builders never end, so this is never past.
			startedMsAgo: computed(() => -startsInMs()),
			startsInMs,
			status: computed(() => (currentItem() == null ? 'current' : 'upcoming')),
		};
	}
}

/**
 * Day-off fallback shown whenever no independent meetup is currently active.
 */
export class WonderNoSchoolScheduleItem extends WonderActivityScheduleItem {
	public readonly calendarEventTitle: string;
	public readonly type = 'noSchool';
	public readonly educator = null;
	public readonly label = 'No School Today';
	public override readonly moment: WonderActivityTimeSignals;
	public readonly resourceLink = null;
	public readonly subject = null;

	constructor(
		dayStart: Temporal.ZonedDateTime,
		private readonly daysActivities: WonderActivityScheduleItem[],
		currentTime: Signal<Temporal.ZonedDateTime>,
		calendarEventTitle = 'School Closed',
	) {
		super(
			dayStart,
			dayStart.add({ days: 1 }),
			`no-school-${dayStart.toPlainDate().toString()}`,
			{ currentTime, source: 'system' },
		);

		this.calendarEventTitle = calendarEventTitle.trim() || 'School Closed';
		this.moment = this.noSchoolMoment(currentTime);
	}

	private noSchoolMoment(
		currentTime: Signal<Temporal.ZonedDateTime>,
	): WonderActivityTimeSignals {
		function getMillisecondsBetween(
			left: Temporal.ZonedDateTime,
			right: Temporal.ZonedDateTime,
		): number {
			return Number(
				(left.toInstant().epochNanoseconds -
					right.toInstant().epochNanoseconds) /
					BigInt(1000000),
			);
		}

		const currentItem = computed(() =>
			this.daysActivities.find((activity) => activity.moment.isCurrent()),
		);
		const status = computed<WonderActivityScheduleItemStatus>(() => {
			const now = currentTime();

			if (Temporal.ZonedDateTime.compare(now, this.startDate) < 0) {
				return 'upcoming';
			}

			if (Temporal.ZonedDateTime.compare(now, this.endDate) >= 0) {
				return 'past';
			}

			return currentItem() == null ? 'current' : 'upcoming';
		});
		const startsInMs = computed(() => {
			const item = currentItem();

			if (item != null) {
				return getMillisecondsBetween(item.endDate, currentTime());
			}

			return Temporal.ZonedDateTime.compare(currentTime(), this.startDate) < 0
				? getMillisecondsBetween(this.startDate, currentTime())
				: 0;
		});

		return {
			endedMsAgo: computed(() =>
				getMillisecondsBetween(currentTime(), this.endDate),
			),
			isCurrent: computed(() => status() === 'current'),
			isPast: computed(() => status() === 'past'),
			startedMsAgo: computed(() => -startsInMs()),
			startsInMs,
			status,
		};
	}
}

/**
 * Scheduled meetup attendance, either virtual or in person.
 */
export class WonderMeetupScheduleItem extends WonderActivityScheduleItem {
	constructor(
		startDate: Temporal.ZonedDateTime,
		endDate: Temporal.ZonedDateTime,
		id: string | number,
		public readonly meetup: MeetupManagementListItemModel,
		currentTime: Signal<Temporal.ZonedDateTime>,
	) {
		super(startDate, endDate, id, { currentTime, source: 'meetup' });
	}

	public get type(): WonderActivityScheduleItemType {
		return this.meetup.type === 'physical' ? 'meetupPhysical' : 'meetupVirtual';
	}

	public get label(): string {
		return (
			(meetupTitles as Record<string, string>)[this.meetup.gathering_type] ??
			'Meetup'
		);
	}

	public get educator(): string | null {
		return this.meetup.host.name.trim();
	}

	public get resourceLink(): WonderActivityScheduleItemResourceLink | null {
		if (this.meetup.type === 'virtual') {
			return {
				href: this.meetup.link,
				target: '_blank',
				type: 'href',
			};
		} else if (this.meetup.type === 'physical') {
			return {
				meetup: this.meetup,
				modalType: 'meetupAttendance',
				type: 'modal',
			};
		}

		return null;
	}

	public readonly subject = null;
}

/**
 * Scheduled classroom pod time that is not tied to a single course.
 */
export class WonderPodScheduleItem extends WonderActivityScheduleItem {
	public readonly type = 'pod';

	constructor(
		startDate: Temporal.ZonedDateTime,
		endDate: Temporal.ZonedDateTime,
		id: string | number,
		public readonly meetup: MeetupManagementListItemModel | null,
		classroomActivity: ClassroomActivityModel,
		period: ClassroomPeriodModel,
		currentTime: Signal<Temporal.ZonedDateTime>,
	) {
		super(startDate, endDate, id, {
			classroomActivity,
			currentTime,
			period,
			source: 'classroom',
		});
	}

	public get educator(): string | null {
		return (
			this.meetup?.host?.name?.trim() ||
			this.classroomActivity?.associated_meetup_host_name?.trim() ||
			null
		);
	}

	public get label(): string {
		return (
			(this.meetup == null
				? null
				: (meetupTitles as Record<string, string>)[
						this.meetup.gathering_type
					]) ||
			this.classroomActivity?.description?.trim() ||
			'Pod Hub'
		);
	}

	public get resourceLink(): WonderActivityScheduleItemResourceLink | null {
		if (this.meetup?.type === 'virtual') {
			return {
				href: this.meetup.link,
				target: '_blank',
				type: 'href',
			};
		} else if (this.meetup?.type === 'physical') {
			return {
				meetup: this.meetup,
				modalType: 'meetupAttendance',
				type: 'modal',
			};
		}

		const href =
			this.classroomActivity?.associated_meetup_link?.trim() ||
			this.classroomActivity?.meetup_link?.trim();

		return href == null || href === ''
			? null
			: {
					href,
					target: '_blank',
					type: 'href',
				};
	}

	public readonly subject = null;
}

/**
 * Scheduled flexible time, such as lunch or other unstructured blocks.
 */
export class WonderUnstructuredScheduleItem extends WonderActivityScheduleItem {
	public readonly type = 'unstructured';

	constructor(
		startDate: Temporal.ZonedDateTime,
		endDate: Temporal.ZonedDateTime,
		id: string | number,
		classroomActivity: ClassroomActivityModel,
		period: ClassroomPeriodModel,
		private readonly itemDescription: string,
		currentTime: Signal<Temporal.ZonedDateTime>,
	) {
		super(startDate, endDate, id, {
			classroomActivity,
			currentTime,
			period,
			source: 'classroom',
		});
	}

	public readonly educator = null;
	public readonly resourceLink = null;
	public readonly subject = null;

	public get label(): string {
		return this.itemDescription;
	}
}

/**
 * Scheduled recess time.
 */
export class WonderRecessScheduleItem extends WonderActivityScheduleItem {
	public readonly type = 'recess';

	constructor(
		startDate: Temporal.ZonedDateTime,
		endDate: Temporal.ZonedDateTime,
		id: string | number,
		classroomActivity: ClassroomActivityModel,
		period: ClassroomPeriodModel,
		private readonly itemDescription: string,
		currentTime: Signal<Temporal.ZonedDateTime>,
	) {
		super(startDate, endDate, id, {
			classroomActivity,
			currentTime,
			period,
			source: 'classroom',
		});
	}

	public readonly educator = null;
	public readonly resourceLink = null;
	public readonly subject = null;

	public get label(): string {
		return this.itemDescription;
	}
}

/**
 * Scheduled study time with an optional external resource.
 */
export class WonderStudyScheduleItem extends WonderActivityScheduleItem {
	public readonly type = 'study';

	constructor(
		startDate: Temporal.ZonedDateTime,
		endDate: Temporal.ZonedDateTime,
		id: string | number,
		classroomActivity: ClassroomActivityModel,
		period: ClassroomPeriodModel,
		private readonly itemDescription: string,
		currentTime: Signal<Temporal.ZonedDateTime>,
	) {
		super(startDate, endDate, id, {
			classroomActivity,
			currentTime,
			period,
			source: 'classroom',
		});
	}

	public readonly educator = null;
	public readonly subject = null;

	public get label(): string {
		return this.itemDescription;
	}

	public get resourceLink(): WonderActivityScheduleItemResourceLink | null {
		const href = this.classroomActivity?.meetup_link?.trim();

		return href == null || href === ''
			? null
			: {
					href,
					target: '_blank',
					type: 'href',
				};
	}
}
